import React, { useMemo, useState, useEffect, ChangeEvent } from "react";
import classNames from "classnames";
import { Button, Typography } from "components/atoms";
import {
  CloseIcon,
  MinusIcon,
  PauseIcon,
  PlayIcon,
  PlusIcon,
} from "components/atoms/Icon";
import "./index.scss";
import { useAppDispatch, useAppSelector } from "hooks";
import {
  incrementMinutesByAmount,
  handleGlobalModalClose,
  handleGlobalTimePause,
  handleGlobalTimeSubmit,
  incrementHours,
  incrementMinutes,
  incrementSeconds,
  decrementHours,
  decrementMinutes,
  decrementSeconds,
  changeMinutes,
  changeSeconds,
  changeHours,
} from "state/reducers/studyTimerSlice";
import { counter } from "worker/timerWorker";
import { persistor } from "state/store";
import { mixpanelHelper } from "utils/functions/mixPanelHelper";
import { decodeToken } from "utils/functions/decodeToken";

interface IProps {
  theme: "light" | "dark";
}

export const Timer = ({ theme }: IProps) => {
  const { hours, minutes, seconds, isModalOpen, isTimePause, isTimeSubmit } =
    useAppSelector(
      (state: {
        studyTimer: {
          hours: number;
          minutes: number;
          seconds: number;
          isTimeSubmit: boolean;
          isTimePause: boolean;
          isModalOpen: boolean;
        };
      }) => state.studyTimer
    );
  const dispatch: any = useAppDispatch();

  const payload = decodeToken();

  const userId = payload ? payload.userid : null;
  const productCode = payload ? payload.productcode : null;
  const offeringCode = payload ? payload.offeringcode : null;

  const handleBtnClick = () => {
    dispatch(handleGlobalTimeSubmit(true));
    dispatch(handleGlobalTimePause(false));
    dispatch(handleGlobalModalClose(false));
    counter?.postMessage({
      action: "start",
      seconds: seconds,
      minutes: minutes,
      hours: hours,
    });
    mixpanelHelper.track("Start Study Timer", {
      timerLength: "",
      testType: "",
      productCode: productCode,
      psoCode: offeringCode,
      userID: userId,
    });
  };

  const formatTime = (value: number) => {
    return value.toString().padStart(2, "0");
  };

  const handleTimeUpDown = (clickType: string) => {
    if (clickType === "plus") {
      if (hours < 99) {
        if ((minutes + 1) % 60 === 0) {
          dispatch(changeMinutes(0));
          dispatch(incrementHours());
        } else {
          dispatch(incrementMinutes());
        }
      }
    } else if (clickType === "minus") {
      if (minutes !== 0) {
        dispatch(decrementMinutes());
      } else {
        if (hours !== 0) {
          dispatch(decrementHours());
          dispatch(changeMinutes(59));
        }
      }
    }
  };

  const handelTimerModalClose = () => {
    dispatch(handleGlobalModalClose(false));
    dispatch(changeSeconds(0));
    dispatch(changeMinutes(0));
    dispatch(changeHours(0));
    dispatch(handleGlobalTimeSubmit(false));
    dispatch(handleGlobalTimePause(false));

    counter?.postMessage({
      action: "pause",
    });
  };

  const handleInputSeconds = (event: ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value, 10);
    if (value > 59) {
      dispatch(changeSeconds(59));
    } else {
      dispatch(changeSeconds(value));
    }
  };

  const handleInputMinutes = (event: ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value, 10);
    if (value > 59) {
      dispatch(changeMinutes(59));
    } else {
      dispatch(changeMinutes(value));
    }
  };

  const handleInputHours = (event: ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value, 10);

    if (value > 99) {
      dispatch(changeHours(99));
    } else {
      dispatch(changeHours(value));
    }
  };

  return (
    <div className="timer-outer-wrapper">
      {!isTimeSubmit && (
        <div className="study-timer-modal">
          <div className="study-timer-header">
            <div className="study-timer-header-typography">
              <div className="title-typography-wrapper">
                <Typography
                  label="Study Timer"
                  variant="h6Medium"
                  styles="study-timer-header-typography-custom-style"
                />
              </div>
            </div>
            <div
              className="study-timer-header-close-icon"
              onClick={handelTimerModalClose}
            >
              <CloseIcon theme={theme} />
            </div>
          </div>
          <div className="study-timer-contain-container">
            <div className="description-section">
              <Typography
                label="It is important to stay focused and keep all distractions away. Turn off all other electronics for best study strategy. Select your study time, then select Start Timer below."
                variant="h8Book"
                styles="custom-typography-style"
              />
            </div>
            <div className="timer-section">
              <div
                className="minus-icon"
                onClick={() => handleTimeUpDown("minus")}
              >
                <MinusIcon theme={theme} />
              </div>
              <div className="timer">
                <input
                  type="number"
                  value={formatTime(hours)}
                  min={0}
                  onChange={(e) => handleInputHours(e)}
                />
                <Typography label={` : `} variant="h2Med" />
                <input
                  type="number"
                  value={formatTime(minutes)}
                  min={0}
                  max={59}
                  onChange={(e) => handleInputMinutes(e)}
                />
                <Typography label={` : `} variant="h2Med" />
                <input
                  type="number"
                  value={formatTime(seconds)}
                  min={0}
                  max={59}
                  onChange={(e) => handleInputSeconds(e)}
                />
              </div>
              <div
                className="plus-icon"
                onClick={() => handleTimeUpDown("plus")}
              >
                <PlusIcon theme={theme} />
              </div>
            </div>
            <div className="timer-btn-section">
              <Button
                label="Start Timer"
                icon={false}
                type="score"
                onClick={handleBtnClick}
                styles="custom-timer-btn"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
